import { useState, useEffect } from 'react';
import { Trash, PencilSimple, X, FloppyDisk, MapPin } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';

const EMPTY = { name: '', city: '', country: 'India', address: '', phone: '', email: '', is_hq: false };

export default function CMSOfficesPanel({ token }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);

  const fetchItems = async () => {
    try { const res = await api.get('/cms/offices'); setItems(res.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleSave = async () => {
    try {
      if (editing) await api.put(`/admin/cms/offices/${editing}?token=${token}`, form);
      else await api.post(`/admin/cms/offices?token=${token}`, form);
      setEditing(null); setForm(EMPTY); fetchItems();
    } catch (e) { alert('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this office?')) return;
    try { await api.delete(`/admin/cms/offices/${id}?token=${token}`); fetchItems(); }
    catch (e) { alert('Failed to delete'); }
  };

  if (loading) return <div className="text-center py-12 text-[#4A5568]">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="cms-offices-panel">
      <div className="bg-white border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-[#0A1F3D] mb-4">{editing ? 'Edit Office' : 'Add Office/Branch'}</h3>
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-[#4A5568] mb-1">Office Name *</label>
            <Input data-testid="office-name-input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="e.g. Kochi Branch" className="rounded-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5568] mb-1">City</label>
            <Input value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} placeholder="City" className="rounded-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5568] mb-1">Country</label>
            <Input value={form.country} onChange={(e) => setForm({...form, country: e.target.value})} placeholder="Country" className="rounded-none" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-[#4A5568] mb-1">Address</label>
            <Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} placeholder="Full address" className="rounded-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5568] mb-1">Phone</label>
            <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="Phone" className="rounded-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5568] mb-1">Email</label>
            <Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="Email" className="rounded-none" />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_hq} onChange={(e) => setForm({...form, is_hq: e.target.checked})} className="w-4 h-4 accent-[#D4AF37]" />
              <span className="text-sm text-[#0A1F3D] font-medium">Headquarters</span>
            </label>
          </div>
        </div>
        <div className="flex gap-2">
          <Button data-testid="office-save-btn" onClick={handleSave} disabled={!form.name} className="bg-[#D4AF37] text-[#0A1F3D] hover:bg-[#c5a033] rounded-none">
            <FloppyDisk size={16} /> {editing ? 'Update' : 'Add Office'}
          </Button>
          {editing && <Button variant="outline" onClick={() => { setEditing(null); setForm(EMPTY); }} className="rounded-none"><X size={16} /> Cancel</Button>}
        </div>
      </div>

      <div className="bg-white border border-slate-200">
        <div className="p-4 border-b border-slate-200"><h3 className="font-bold text-[#0A1F3D]">{items.length} Offices</h3></div>
        {items.length === 0 ? (
          <div className="text-center py-8 text-[#4A5568]">No offices yet.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((item) => (
              <div key={item.id} className="flex items-center gap-4 p-4 hover:bg-[#F5F5F5]/50">
                <MapPin size={20} className="text-[#D4AF37] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-[#0A1F3D] text-sm">{item.name} {item.is_hq && <span className="text-xs bg-[#D4AF37] text-[#0A1F3D] px-2 py-0.5 ml-2">HQ</span>}</h4>
                  <p className="text-xs text-[#4A5568]">{item.address || `${item.city}, ${item.country}`}</p>
                  <p className="text-xs text-[#4A5568]">{item.phone} {item.email && `| ${item.email}`}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => { setEditing(item.id); setForm({ name: item.name, city: item.city, country: item.country, address: item.address, phone: item.phone, email: item.email, is_hq: item.is_hq }); }} className="p-2 hover:bg-slate-100 text-[#4A5568] hover:text-[#0A1F3D]"><PencilSimple size={16} /></button>
                  <button onClick={() => handleDelete(item.id)} className="p-2 hover:bg-red-50 text-[#4A5568] hover:text-red-500"><Trash size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
